import { useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { setSearchSeen } from "@/api/endpoints/searches";
import { useSearches } from "@/hooks/useSearches";

const SEEN_DELAY_MS = 3000;

type MarkSearchSeenOptions = {
  orgUuid: Uuid;
  searchUuid: Uuid;
  enabled: boolean;
};

export function useMarkSearchSeen({
  orgUuid,
  searchUuid,
  enabled,
}: MarkSearchSeenOptions) {
  // Server state
  const queryClient = useQueryClient();

  const { data: searchesData } = useSearches(orgUuid);

  const { mutate: markSearchSeen } = useMutation({
    mutationFn: () => setSearchSeen(searchUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [orgUuid, "searches"],
        exact: true,
      });
    },
  });

  // Helpers
  const hasUnread = searchesData?.ok
    ? searchesData.data.find((search) => search.uuid === searchUuid)?.has_unread
    : undefined;

  useEffect(() => {
    if (!enabled || !hasUnread) return;
    const timeout = setTimeout(() => markSearchSeen(), SEEN_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [enabled, hasUnread, markSearchSeen]);
}
